import React from 'react';
import { useTranslation } from 'react-i18next';

import { Design } from '../../../../../../viewer/elements/design';
import { useSharedView } from '../../../../../hooks/use-shared-view';
import { useUnitSymbols } from '../../../../../hooks/use-unit-symbols';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { AssetTags } from '../../components/asset-tags';
import { AssetVolumes } from '../../components/asset-volume/asset-volumes';
import { OpacitySection } from '../../components/opacity-section';
import { FullWidth } from '../../styles/grid';
import { numberOrDefault } from '../../utils/formatting';

type Props = {
  asset: Design;
  refreshAsset: () => void;
};

const DesignBody = (props: Props) => {
  const { t } = useTranslation();
  const { isSharedView } = useSharedView();
  const unitSymbols = useUnitSymbols();
  const isLoaded = props.asset.loadingStatus === 'loaded';

  return (
    <>
      <LabelledContainer text={t('design.section.surface3d', { ns: 'skyviewAssetMenu' })}>
        {numberOrDefault(isLoaded ? props.asset.metaData.area : undefined, unitSymbols.area, 2)}
      </LabelledContainer>

      <LabelledContainer text={t('design.section.surface2d', { ns: 'skyviewAssetMenu' })}>
        {numberOrDefault(
          isLoaded ? props.asset.metaData.planarArea : undefined,
          unitSymbols.area,
          2,
        )}
      </LabelledContainer>

      <LabelledContainer text={t('design.section.length3d', { ns: 'skyviewAssetMenu' })}>
        {numberOrDefault(
          isLoaded ? props.asset.metaData.lineLength : undefined,
          unitSymbols.length,
          2,
        )}
      </LabelledContainer>

      <LabelledContainer text={t('design.section.length2d', { ns: 'skyviewAssetMenu' })}>
        {numberOrDefault(
          isLoaded ? props.asset.metaData.linePlanarLength : undefined,
          unitSymbols.length,
          2,
        )}
      </LabelledContainer>

      <LabelledContainer text={t('design.section.triangles', { ns: 'skyviewAssetMenu' })}>
        {numberOrDefault(isLoaded ? props.asset.metaData.triangleCount : undefined, 'st', 0)}
      </LabelledContainer>

      <LabelledContainer text={t('design.section.points', { ns: 'skyviewAssetMenu' })}>
        {numberOrDefault(isLoaded ? props.asset.metaData.pointCount : undefined, 'st', 0)}
      </LabelledContainer>

      {!isSharedView && props.asset.assetId && (
        <FullWidth>
          <LabelledContainer text={t('fileManager.actionBar.tags', { ns: 'components' })}>
            <AssetTags
              initialTagsIds={props.asset.assetTagIds}
              refreshAsset={props.refreshAsset}
              target={{ kind: 'dynamicAsset', id: props.asset.assetId, name: props.asset.name }}
            />
          </LabelledContainer>
        </FullWidth>
      )}
      <FullWidth>
        <OpacitySection asset={props.asset} />
      </FullWidth>

      <FullWidth>
        <AssetVolumes asset={props.asset} />
      </FullWidth>
    </>
  );
};

export { DesignBody, Props as DesignBodyProps };
